'use client';

import { useEffect } from 'react';
import { toast } from 'sonner';
import { notify } from '@/components/ui/BrandToaster';
import { apiClient } from '@/lib/apiClient';
import { suscribirSesionExpirada } from '@/lib/sesion-expirada';

// Id fijo: si la sesión vuelve a fallar con el aviso arriba, sonner actualiza la
// misma tarjeta en vez de apilar otra.
const AVISO_ID = 'sesion-expirada';

function abrirLoginEnOtraPestana() {
	const next = window.location.pathname + window.location.search;
	window.open(`/login?next=${encodeURIComponent(next)}`, '_blank', 'noopener');
}

/**
 * Aviso de sesión vencida DE VERDAD (la API rechazó el refresh; ver `apiClient`).
 * Se monta una sola vez, en el layout raíz.
 *
 * Decisión: nada visible hasta que la persona intenta CAMBIAR algo. Una lectura en
 * segundo plano que falla no muestra nada; un guardado que falla muestra un aviso
 * fijo con un botón que abre el login en OTRA pestaña.
 *
 * No toca `AuthSessionProvider` ni navega, a propósito: con el usuario en null,
 * `RoleGuard` desmonta la página —páginas de admin, modales abiertos— y se pierde
 * lo que la persona tenía sin guardar. Así, inicia sesión al lado, vuelve, y
 * reintenta con su formulario intacto.
 *
 * Mientras el aviso está arriba, cada vez que la ventana recupera el foco se
 * pregunta a `/auth/me` si ya hay sesión (las rutas `/auth/` no disparan refresh).
 * Cuando la hay, el aviso se cierra solo.
 */
export function SesionExpiradaAviso() {
	useEffect(() => {
		let dejarDeEsperar: (() => void) | null = null;

		const esperarSesion = () => {
			if (dejarDeEsperar) return;

			let consultando = false;
			const alEnfocar = async () => {
				if (consultando) return;
				consultando = true;
				try {
					const res = await apiClient('/auth/me');
					if (!res.ok) return;
					toast.dismiss(AVISO_ID);
					dejarDeEsperar?.();
				} catch {
					// Sin red: se vuelve a preguntar en el próximo foco.
				} finally {
					consultando = false;
				}
			};

			window.addEventListener('focus', alEnfocar);
			dejarDeEsperar = () => {
				window.removeEventListener('focus', alEnfocar);
				dejarDeEsperar = null;
			};
		};

		const desuscribir = suscribirSesionExpirada(({ mutacion }) => {
			if (!mutacion) return;
			notify.warning('Tu sesión expiró', {
				id: AVISO_ID,
				duration: Infinity,
				description:
					'El último cambio no se guardó. Inicia sesión en otra pestaña y vuelve aquí para reintentarlo: lo que tienes en pantalla se conserva.',
				action: { label: 'Iniciar sesión', onClick: abrirLoginEnOtraPestana },
				// Cerrado a mano (X) o por el foco con sesión: se deja de consultar /auth/me.
				onDismiss: () => dejarDeEsperar?.(),
			});
			esperarSesion();
		});

		return () => {
			desuscribir();
			dejarDeEsperar?.();
		};
	}, []);

	return null;
}
